import { Calculator } from './Calculator';

export function GraphicsUtil() {}

/* Shapes */

GraphicsUtil.createSprite = function () {
  return new createjs.Shape();
};

GraphicsUtil.drawRect = function (sprite, x, y, width, height) {
  sprite.graphics.beginStroke('white');
  sprite.graphics.drawRect(x, y, width, height);
  sprite.graphics.endStroke();
};

GraphicsUtil.drawCircle = function (sprite, radius, x?, y?) {
  sprite.graphics.beginStroke('white');
  if (x == null) x = 0;
  if (y == null) y = 0;
  sprite.graphics.drawCircle(x, y, radius);
  sprite.graphics.endStroke();
};

GraphicsUtil.drawLine = function (sprite, fromX, fromY, toX, toY, color) {
  sprite.graphics.setStrokeStyle(2).beginStroke(color);
  sprite.graphics.moveTo(fromX, fromY);
  sprite.graphics.lineTo(toX, toY);
  sprite.graphics.endStroke();
};

GraphicsUtil.drawPolygon = function (
  sprite,
  numPoints,
  radius,
  angle?,
  x?,
  y?,
) {
  if (angle == null) angle = 0;
  if (x == null) x = 0;
  if (y == null) y = 0;
  sprite.graphics.beginStroke('white');
  sprite.graphics.moveTo(
    Math.cos(angle) * radius + x,
    Math.sin(angle) * radius + y,
  );
  for (let p = 0; p < numPoints; p++) {
    sprite.graphics.lineTo(
      Math.cos(angle) * radius + x,
      Math.sin(angle) * radius + y,
    );
    angle += (2 * Math.PI) / numPoints;
  }
  sprite.graphics.lineTo(
    Math.cos(angle) * radius + x,
    Math.sin(angle) * radius + y,
  );
  sprite.graphics.endStroke();
};

GraphicsUtil.drawWedge = function (
  sprite,
  radius,
  cx,
  cy,
  arc,
  startAngle = 0,
) {
  sprite.graphics.beginStroke('white');
  GraphicsUtil._drawWedge(sprite.graphics, cx, cy, radius, arc, startAngle);
  sprite.graphics.endStroke();
};

GraphicsUtil._drawWedge = function (
  target,
  x,
  y,
  radius,
  arc,
  startAngle,
  yRadius?,
) {
  startAngle = startAngle || 0;
  yRadius = yRadius || 0;
  // if yRadius is undefined, yRadius = radius
  if (yRadius == 0) {
    yRadius = radius;
  }

  // move to x,y position
  target.moveTo(x, y);
  // if yRadius is undefined, yRadius = radius
  if (yRadius == 0) {
    yRadius = radius;
  }
  // Init vars
  let segAngle, theta, angle, angleMid, segs, ax, ay, bx, by, cx, cy;
  // limit sweep to reasonable numbers
  if (Math.abs(arc) > 360) {
    arc = 360;
  }
  // Flash uses 8 segments per circle, to match that, we draw in a maximum
  // of 45 degree segments. First we calculate how many segments are needed
  // for our arc.
  segs = Math.ceil(Math.abs(arc) / 45);
  // Now calculate the sweep of each segment.
  segAngle = arc / segs;
  // The math requires radians rather than degrees. To convert from degrees
  // use the formula (degrees/180)*Math.PI to get radians.
  theta = -(segAngle / 180) * Math.PI;
  // convert angle startAngle to radians
  angle = -(startAngle / 180) * Math.PI;
  // draw the curve in segments no larger than 45 degrees.
  if (segs > 0) {
    // draw a line from the center to the start of the curve
    ax = x + Math.cos((startAngle / 180) * Math.PI) * radius;
    ay = y + Math.sin((-startAngle / 180) * Math.PI) * yRadius;
    target.lineTo(ax, ay);
    // Loop for drawing curve segments
    for (let i = 0; i < segs; ++i) {
      angle += theta;
      angleMid = angle - theta / 2;
      bx = x + Math.cos(angle) * radius;
      by = y + Math.sin(angle) * yRadius;
      cx = x + Math.cos(angleMid) * (radius / Math.cos(theta / 2));
      cy = y + Math.sin(angleMid) * (yRadius / Math.cos(theta / 2));
      target.curveTo(cx, cy, bx, by);
    }
    // close the wedge by drawing a line to the center
    target.lineTo(x, y);
  }
};

GraphicsUtil.beginFill = function (sprite, color, alpha?) {
  sprite.graphics.beginFill(GraphicsUtil.toHexString(color), alpha);
};
GraphicsUtil.beginGradientFill = function (sprite, color) {
  const colors = GraphicsUtil.getGradientColors(color);
  const ratios = [0.25, 1];
  sprite.graphics.beginLinearGradientFill(colors, ratios, 0, 80, 0, -225);
};

/* Color */
GraphicsUtil.toColor = function (colorString) {
  return Calculator.getInt(colorString);
};
GraphicsUtil.toHexString = function (number: number) {
  let numStr = number.toString(16);
  for (let i = numStr.length; i < 6; i++) {
    numStr = '0' + numStr;
  }
  return '#' + numStr;
};

GraphicsUtil.getGradientColors = function (color: number) {
  let color2;
  const r = GraphicsUtil.extractRed(color);
  const g = GraphicsUtil.extractGreen(color);
  const b = GraphicsUtil.extractBlue(color);
  if (r > g && r > b) {
    if (r / (b + g) > 1.5 && (b >= 0x33 || g <= 0x33)) color2 = 0x9900ff;
    else color2 = 0xff0000;
  } else {
    if (b / (r + g) > 1.5) color2 = 0xff0099;
    else color2 = 0x0000ff;
  }
  return [GraphicsUtil.toHexString(color), GraphicsUtil.toHexString(color2)];
};

GraphicsUtil.extractRed = function (color: number) {
  return (color >> 16) & 0xff;
};
GraphicsUtil.extractGreen = function (color: number) {
  return (color >> 8) & 0xff;
};
GraphicsUtil.extractBlue = function (color: number) {
  return color & 0xff;
};
GraphicsUtil.lighten = function (color: number, amount?: number) {
  if (amount === undefined) amount = 0.5;
  return GraphicsUtil.scaleColor(color, amount);
};
GraphicsUtil.darken = function (color: number, amount?: number) {
  if (amount === undefined) amount = 0.5;
  return GraphicsUtil.scaleColor(color, -amount);
};

GraphicsUtil.scaleColor = function (color: number, scale: number) {
  let r = (color & 0xff0000) >> 16;
  let g = (color & 0x00ff00) >> 8;
  let b = color & 0x0000ff;
  r += 255 * scale * ((g + b) / (r + g + b));
  r = r > 255 ? 255 : r;
  r = r < 0 ? 0 : r;
  g += 255 * scale * ((r + b) / (r + g + b));
  g = g > 255 ? 255 : g;
  g = g < 0 ? 0 : g;
  b += 255 * scale * ((r + g) / (r + g + b));
  b = b > 255 ? 255 : b;
  b = b < 0 ? 0 : b;
  return ((r << 16) & 0xff0000) + ((g << 8) & 0x00ff00) + (b & 0x0000ff);
};

/* Filters */

GraphicsUtil.tint = function (mc, color, alpha) {
  //Unsupported
  /*
	if(color){
		var r = GraphicsUtil.extractRed(color);
		var g = GraphicsUtil.extractGreen(color);
		var b = GraphicsUtil.extractBlue(color);
		var colorFilter = new createjs.ColorFilter((1 - alpha) * (r / 0xFF), (1 - alpha) * (g / 0xFF), (1 - alpha) * (b / 0xFF), 1, alpha * r, alpha * g, alpha * b);
		mc.filters = [colorFilter];
		//mc.updateCache();
		//mc.uncache();
		mc.cache(-500, -500, 1000, 1000);
	}else{
		mc.filters = [];	
	}
	*/
};

GraphicsUtil.untint = function (mc, color, alpha) {};

GraphicsUtil.shadow = function (
  movieClip,
  alpha: number,
  blur = 4.0,
  distance = 2,
) {
  movieClip.shadow = new createjs.Shadow(
    `rgba(0,0,0,${alpha})`,
    distance,
    distance,
    blur,
  );
};
